<template>
  <div class="container mt-5">
    <transition name="fade">
      <MenssageAlert :messages="messages" />
    </transition>
    <CContainer>
      <InlineNavigation
        routeBackName="automationList"
        :backRouteTranslation="$t('automation.statistics.nav.automationList')"
        :currentRouteTranslation="`${$t('automation.statistics.nav.currentAutomation')}: ${automationName || '-'}`"
      />

      <AutomationStatisticsHeader :headerData="headerData" :isLoadingData="loadingWorkflowData" />

      <div class="d-flex gap-40">
        <AutomationStatisticsChart class="flex-grow-1" :statisticsProperties="periodStatisticsProperties" :isLoadingData="loadingPeriodStatistics" @periodChanged="setStatisticsChartPeriod" />
        <AutomationStatisticsGeneral
          class="flex-basis-col-2"
          :statisticsProperties="generalStatisticsProperties"
          :isLoadingData="loadingGeneralStatistics"
        />
      </div>
    </CContainer>
  </div>
</template>
<script>
import InlineNavigation from '../../../components/InlineNavigation.vue'
import AutomationStatisticsHeader from '../components/AutomationStatisticsHeader.vue'
import AutomationStatisticsGeneral from '../components/AutomationStatisticsGeneral.vue'
import AutomationStatisticsChart from '../components/AutomationStatisticsChart/Index.vue'
import MenssageAlert from '@/components/MenssageAlert'
import services from '@/services'
import { getErrorMessage } from '@/utils/errorHandling.js'
import { CSpinner } from '@coreui/vue'
import { WORKFLOW_TYPES, STATISTICS_TYPES, STATISTICS_EMAIL_PROPERTIES, STATISTICS_SMS_PROPERTIES } from '../constants'

export default {
  name: 'AutomationStatistics',
  components: { AutomationStatisticsHeader, AutomationStatisticsGeneral, AutomationStatisticsChart, InlineNavigation, CSpinner, MenssageAlert },
  data() {
    return {
      workflowData: undefined,
      generalStatistics: undefined,
      generalStatisticsProperties: undefined,
      periodStatistics: undefined,
      periodStatisticsProperties: undefined,
      loadingWorkflowData: false,
      loadingGeneralStatistics: false,
      loadingPeriodStatistics: false,
      messages: [],
      statisticsChartPeriod: undefined,
    }
  },
  mounted() {
    this.getPageData()
  },
  methods: {
    async getPageData() {
      this.loadingGeneralStatistics = true
      this.loadingPeriodStatistics = true

      await this.getWorkflowData()
      this.getGeneralStatistics()
      this.getPeriodStatistics()
    },
    async getWorkflowData() {
      try {
        this.loadingWorkflowData = true
        const response = await services.getAutomation(this.organizationId, this.workflowId)
        this.workflowData = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.loadingWorkflowData = false
      }
    },
    async getPeriodStatistics() {
      try {
        this.loadingPeriodStatistics = true
        //TODO: Lidar com múltiplos campaignIdFuturamente
        const params = {
          channels: [this.automationType],
          startDatetime: this.statisticsChartPeriod.periodStart,
          endDatetime: this.statisticsChartPeriod.periodEnd
        }
        const response = await services.getCampaignResultsByPeriod(this.organizationId, this.workflowData?.campaigns[0]?.campaignId, params)

        this.periodStatistics = response.data.data
        this.periodStatisticsProperties = this.getStatisticsProperties(this.periodStatistics)
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.loadingPeriodStatistics = false
      }
    },
    setStatisticsChartPeriod(period) {
      this.statisticsChartPeriod = period

      if(this.workflowData) this.getPeriodStatistics()
    },
    async getGeneralStatistics() {
      try {
        this.loadingGeneralStatistics = true
        //TODO: Lidar com múltiplos campaignIdFuturamente
        const params = {
          channels: [this.automationType],
        }
        const response = await services.getCampaignResults(this.organizationId, this.workflowData?.campaigns[0]?.campaignId, params)

        this.generalStatistics = response.data.data
        this.generalStatisticsProperties = this.getStatisticsProperties(this.generalStatistics)
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.loadingGeneralStatistics = false
      }
    },
    getStatisticsProperties(statisticsData) {
      if (!statisticsData?.channels) return
      
      if (this.automationType == WORKFLOW_TYPES.SEND_EMAIL) {
        return this.getPropertiesFromChannel('email', statisticsData.channels.email, STATISTICS_EMAIL_PROPERTIES)
      }

      if (this.automationType == WORKFLOW_TYPES.SEND_SMS) {
        return this.getPropertiesFromChannel('sms', statisticsData.channels.sms, STATISTICS_SMS_PROPERTIES)
      }
    },
    getPropertiesFromChannel(channel, channelData, statisticProperties) {
      const propertiesMap = {
        email: {
          open: STATISTICS_TYPES.OPEN,
          click: STATISTICS_TYPES.CLICK,
          bounced: STATISTICS_TYPES.BOUNCED,
          unsubscribe: STATISTICS_TYPES.UNSUBSCRIBE,
        },
        sms: {
          delivered: STATISTICS_TYPES.DELIVERED,
          notDelivered: STATISTICS_TYPES.NOT_DELIVERED,
          pending: STATISTICS_TYPES.PENDING,
        },
      }
      const properties = propertiesMap[channel]

      if (!properties) return

      const valuedStatisticProperties = statisticProperties.map((property) => {
        return { ...property, value: channelData[property.name] }
      })

      return valuedStatisticProperties
    },
    redirectToAutomationList() {
      this.$router.push({ name: 'automationList' })
    },
  },
  computed: {
    organizationId() {
      return this.$route.params.organizationId
    },
    workflowId() {
      return this.$route.params.workflowId
    },
    automationName() {
      return this.workflowData?.name
    },
    automationType() {
      return this.workflowData?.workflowJson?.steps?.values?.[0]?.type
    },
    headerData() {
      if (!this.workflowData) return null

      return {
        //TODO: Alterar valor lastUpdate
        lastUpdate: this.workflowData.createdDateTime,
        name: this.workflowData.name,
        code: this.workflowData.id,
        triggerTypes: this.workflowData.workflowJson?.triggers?.values,
        state: this.workflowData.workflowStatus,
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.container {
  min-width: 1170px;
}
.gap-15 {
  gap: 15px;
}
.gap-40 {
  gap: 40px;
}
.flex-basis-col-2 {
  flex-basis: 450px;
}
</style>
